/**
 * /dashboard/announcements — tenant announcements feed (G11) + HR-admin authoring.
 *
 * The dashboard hub's <AnnouncementsRibbon /> shows the top 3 pinned
 * ones with a "View all (N)" link that lands here. This page lists
 * EVERY active announcement (pinned-first, then by posted_at desc).
 *
 * HR-admins (isHRAdmin: company_admin / hr_manager) additionally get:
 *   - "New announcement" → Dialog form (title, body, pinned, expiry).
 *   - Per-row Edit (Dialog form) + Delete (AlertDialog — hard delete).
 * Writes go through TanStack useMutation → the CSRF-aware axios client
 * (POST/PATCH/DELETE), invalidating the announcements query on success
 * with sonner toasts. Non-admins keep the read-only list (the backend
 * is the source of truth — write endpoints are hrRole-gated server-side).
 */

import { useMemo, useState } from "react";
import { formatDate } from "@/lib/format";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Loader2,
  Megaphone,
  Pencil,
  Pin,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/page-header";
import {
  type Announcement,
  type CreateAnnouncementRequest,
  type UpdateAnnouncementRequest,
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  updateAnnouncement,
} from "@/lib/api/announcements";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/announcements")({
  component: AnnouncementsPage,
});

const ANNOUNCEMENTS_QUERY_KEY = ["announcements", "list"] as const;

// Mirror the backend bounds (announcementTitleMaxRunes / announcementBodyMaxRunes).
const TITLE_MAX = 200;
const BODY_MAX = 10000;

/** "5 Nov 2026" in India time (lib/format — never the browser's locale). */
function fmtPostedAt(iso: string): string {
  return formatDate(iso, iso);
}

// ---------------------------------------------------------------------------
// Shared form schema (create + edit) — datetime-local <input> ↔ RFC3339.
// ---------------------------------------------------------------------------

const formSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Title is required")
    .max(TITLE_MAX, `Title must be ${TITLE_MAX} characters or fewer`),
  body: z
    .string()
    .trim()
    .min(1, "Body is required")
    .max(BODY_MAX, `Body must be ${BODY_MAX} characters or fewer`),
  is_pinned: z.boolean(),
  // datetime-local value ("YYYY-MM-DDTHH:mm") or "" for no expiry.
  expires_at_local: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const EMPTY_FORM: FormValues = {
  title: "",
  body: "",
  is_pinned: false,
  expires_at_local: "",
};

/** RFC3339 (UTC) → local "YYYY-MM-DDTHH:mm" for a datetime-local input. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Shift to local time then strip to minute precision.
  const tzOffsetMs = d.getTimezoneOffset() * 60 * 1000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

/** local "YYYY-MM-DDTHH:mm" → RFC3339 (UTC), or "" when blank/invalid. */
function localInputToISO(local: string): string {
  const v = local.trim();
  if (v === "") return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Create / Edit dialog (shared form)
// ---------------------------------------------------------------------------

function AnnouncementFormDialog({
  trigger,
  existing,
}: {
  trigger: React.ReactNode;
  /** Present → edit mode; absent → create mode. */
  existing?: Announcement;
}) {
  const isEdit = existing !== undefined;
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
  });

  const seed = (): FormValues =>
    existing
      ? {
          title: existing.title,
          body: existing.body,
          is_pinned: existing.is_pinned,
          expires_at_local: isoToLocalInput(existing.expires_at),
        }
      : EMPTY_FORM;

  const createMut = useMutation({
    mutationFn: (body: CreateAnnouncementRequest) => createAnnouncement(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
      toast.success("Announcement posted.");
      setOpen(false);
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  const updateMut = useMutation({
    mutationFn: (body: UpdateAnnouncementRequest) =>
      updateAnnouncement(existing!.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
      toast.success("Announcement updated.");
      setOpen(false);
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  const pending = createMut.isPending || updateMut.isPending;

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (v) form.reset(seed());
  };

  const onSubmit = (values: FormValues) => {
    const expiresISO = localInputToISO(values.expires_at_local);
    if (isEdit) {
      // PATCH: "" clears the expiry; a value (re)sets it.
      updateMut.mutate({
        title: values.title,
        body: values.body,
        is_pinned: values.is_pinned,
        expires_at: expiresISO,
      });
    } else {
      createMut.mutate({
        title: values.title,
        body: values.body,
        is_pinned: values.is_pinned,
        expires_at: expiresISO === "" ? null : expiresISO,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Announcement" : "New Announcement"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Office closed for Diwali"
                      maxLength={TITLE_MAX}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Body *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Write your company-wide note…"
                      rows={6}
                      maxLength={BODY_MAX}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="expires_at_local"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expires (Optional)</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormDescription>
                    Leave blank to keep the announcement live indefinitely.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_pinned"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(c) => field.onChange(c === true)}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0 font-normal">
                    Pin to the top of the feed and dashboard ribbon
                  </FormLabel>
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                {isEdit ? "Save Changes" : "Post Announcement"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete (AlertDialog)
// ---------------------------------------------------------------------------

function DeleteAnnouncementButton({ announcement }: { announcement: Announcement }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => deleteAnnouncement(announcement.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
      toast.success("Announcement deleted.");
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-destructive hover:text-destructive"
          disabled={del.isPending}
          aria-label={`Delete announcement: ${announcement.title}`}
        >
          {del.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
          <AlertDialogDescription>
            “{announcement.title}” will be permanently removed for everyone in
            your company. This can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => del.mutate()}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function AnnouncementsPage() {
  const { user } = useAuth();
  const isHR = isHRAdmin(user?.role);

  const q = useQuery({
    queryKey: ANNOUNCEMENTS_QUERY_KEY,
    queryFn: ({ signal }) => listAnnouncements(signal),
    staleTime: 60_000,
  });

  // Pinned first, then most-recent-first within each bucket.
  const sorted = useMemo(() => {
    const items = q.data?.items ?? [];
    return [...items].sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return b.posted_at.localeCompare(a.posted_at);
    });
  }, [q.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Announcements"
        description="Notes from your HR-admins, pinned items first."
        actions={
          isHR && (
            <AnnouncementFormDialog
              trigger={
                <Button className="gap-1.5">
                  <Plus aria-hidden className="h-4 w-4" />
                  New Announcement
                </Button>
              }
            />
          )
        }
      />

      {q.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load announcements</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.isSuccess && sorted.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Megaphone className="h-7 w-7" />
            </div>
            <p className="text-base font-medium text-foreground">
              No announcements yet
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              {isHR
                ? "Post a company-wide note and it'll show up here for everyone."
                : "When an HR-admin posts a company-wide note it'll show up here."}
            </p>
            {isHR && (
              <AnnouncementFormDialog
                trigger={
                  <Button variant="outline" className="mt-2 gap-1.5">
                    <Plus aria-hidden className="h-4 w-4" />
                    New Announcement
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {q.isSuccess && sorted.length > 0 && (
        <div className="space-y-3">
          {sorted.map((a) => (
            <Card key={a.id}>
              <CardContent className="space-y-2 pt-5">
                <div className="flex flex-wrap items-center gap-2">
                  {a.is_pinned && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                      <Pin className="h-3 w-3" />
                      Pinned
                    </span>
                  )}
                  <h2 className="text-base font-semibold text-foreground">
                    {a.title}
                  </h2>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {fmtPostedAt(a.posted_at)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {a.body}
                </p>
                {isHR && (
                  <div className="flex justify-end gap-1 pt-1">
                    <AnnouncementFormDialog
                      existing={a}
                      trigger={
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          aria-label={`Edit announcement: ${a.title}`}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Button>
                      }
                    />
                    <DeleteAnnouncementButton announcement={a} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
